import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { RiskBanner } from '@/shared/components/RiskBanner';
import { useToast } from '@/shared/components/Toast';
import { useSubscription } from '@/shared/hooks/useBilling';
import { useAccountState } from '@/shared/hooks/useAccountState';
import { ChooseActiveSheet } from './ChooseActiveSheet';
import { openPaymentMethodPage } from './payment-method';

/** Statuses of a renewal charge that failed and is being retried. */
const PAYMENT_ISSUE = new Set(['past_due', 'pending']);

/**
 * The account's billing problems, on every screen, for its owner:
 * - a renewal charge failed and is being retried (AL-11, M-03): update the
 *   payment method before the plan stops;
 * - the plan holds fewer brands, channels or members than the account has
 *   (D-01): the extra ones are paused; choose what stays active.
 */
export function AccountBillingBanner({ className = '' }: { className?: string }): ReactElement | null {
  const account = useAccountState();
  const { data: subscription } = useSubscription();
  if (!account.known || !account.isAccountOwner || !subscription) return null;
  const paused = subscription.paused ?? { brands: 0, channels: 0, members: 0 };
  const anyPaused = paused.brands + paused.channels + paused.members > 0;
  const paymentIssue = !subscription.comped && PAYMENT_ISSUE.has(String(subscription.status).toLowerCase());
  if (!anyPaused && !paymentIssue) return null;
  return <BannerBody className={className} anyPaused={anyPaused} paymentIssue={paymentIssue} />;
}

/** The banners themselves: only mounted when there is something to say. */
function BannerBody({ className, anyPaused, paymentIssue }: { className: string; anyPaused: boolean; paymentIssue: boolean }): ReactElement {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [choosing, setChoosing] = useState(false);
  const [opening, setOpening] = useState(false);

  const updatePayment = async (): Promise<void> => {
    setOpening(true);
    const ok = await openPaymentMethodPage();
    setOpening(false);
    if (!ok) toast('error', t('settings.billingTab.paymentMethodFailed'));
  };

  return (
    <div className={`space-y-2 ${className}`} data-testid="account-billing-banner">
      {paymentIssue && (
        <RiskBanner
          tone="bad"
          message={t('settings.billingTab.paymentIssue')}
          action={{ label: t('settings.billingTab.updatePaymentMethod'), onClick: () => void updatePayment(), busy: opening }}
        />
      )}
      {anyPaused && (
        <RiskBanner
          message={t('planLimits.banner.owner')}
          action={{ label: t('planLimits.banner.choose'), onClick: () => setChoosing(true) }}
        />
      )}
      <ChooseActiveSheet open={choosing} onClose={() => setChoosing(false)} />
    </div>
  );
}
